import { useTranslation } from 'react-i18next';
import { BookingStatus } from '../types/booking';

interface BookingStatusBadgeProps {
  readonly status: string; // string (not BookingStatus) — tolerates unknown future statuses gracefully
}

// Record<BookingStatus, string> enforces exhaustiveness at compile time — adding a new
// BookingStatus value without updating these tables causes a TS error (Effective TypeScript §28).
const STATUS_STYLES: Record<BookingStatus, string> = {
  CONFIRMED:        'bg-green-100 text-green-700',
  CANCELLED:        'bg-red-100 text-red-700',
  PENDING:          'bg-amber-100 text-amber-700',
  AWAITING_PAYMENT: 'bg-orange-100 text-orange-700',
  CHECKED_IN:       'bg-blue-100 text-blue-700',
  CHECKED_OUT:      'bg-gray-100 text-gray-700',
  COMPLETED:        'bg-teal-100 text-teal-700',
  // 032 deferred-flow statuses
  PENDING_OWNER_CONFIRMATION:    'bg-yellow-100 text-yellow-700',
  AWAITING_GUEST_PAYMENT:        'bg-orange-100 text-orange-700',
  DEPOSIT_PAID_AWAITING_BALANCE: 'bg-indigo-100 text-indigo-700',
};

const KNOWN_STATUSES = Object.keys(STATUS_STYLES) as BookingStatus[];

export const BookingStatusBadge = ({ status }: BookingStatusBadgeProps) => {
  const { t } = useTranslation('badges');

  const STATUS_LABELS: Record<BookingStatus, string> = {
    CONFIRMED:        t('statusConfirmed'),
    CANCELLED:        t('statusCancelled'),
    PENDING:          t('statusPending'),
    AWAITING_PAYMENT: t('statusAwaitingPayment'),
    CHECKED_IN:       t('statusCheckedIn'),
    CHECKED_OUT:      t('statusCheckedOut'),
    COMPLETED:        t('statusCompleted'),
    // 032 deferred-flow statuses
    PENDING_OWNER_CONFIRMATION:    t('statusPendingOwnerConfirmation'),
    AWAITING_GUEST_PAYMENT:        t('statusAwaitingGuestPayment'),
    DEPOSIT_PAID_AWAITING_BALANCE: t('statusDepositPaidAwaitingBalance'),
  };

  const isKnown = KNOWN_STATUSES.includes(status as BookingStatus);
  const classes = isKnown
    ? STATUS_STYLES[status as BookingStatus]
    : 'bg-gray-100 text-gray-600';
  const label = isKnown
    ? STATUS_LABELS[status as BookingStatus]
    : status;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classes}`}>
      {label}
    </span>
  );
};
